// ============================================================================
// hooks/liveStreamManagment/useSlotAudioPreview.ts
//
// Écoute des réglages audio d'un slot PENDANT que sa fenêtre de réglages est ouverte,
// avant « Valider » (même modèle que le brouillon du Mixeur).
//
// Principe : une entrée d'APERÇU `audio-preview-<slot>` est empruntée sur la MÊME source
// que l'entrée réelle (flux partagé : ni second getUserMedia ni second MediaStreamSource).
// Elle porte sa propre chaîne de réglages, à qui on applique le BROUILLON (effets,
// niveaux), et n'est branchée que sur les enceintes de la régie. Elle n'est jamais
// raccordée au bus : `connectRegistryOutput` ne sait brancher que ce que `initAudioMixer`
// lui désigne. Ce qui part à l'antenne reste donc réglé par l'entrée réelle, inchangée
// jusqu'à « Valider ».
//
// Pendant l'aperçu, l'écoute locale de l'entrée réelle est coupée (sinon la source
// s'entendrait deux fois, avec et sans filtres) puis rétablie à la fermeture.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import { useAudioMixer } from '../../contexts/liveStreamManagment/AudioMixerContext';
import { AUDIO_EFFECTS } from '../../components/block/liveStreamManagment/audioEffects/registry';

export interface SlotAudioPreviewDraft {
  effects: Record<string, boolean>;
  effectConfigs: Record<string, unknown>;
  stereo: { left: number; right: number };
  /** Gain mono historique du slot (SourceAudioConfig.volume). */
  volume: number;
  muted: boolean;
  /** Coupe seulement l'écoute locale de l'aperçu (enceintes de la régie) : niveaux et mesures continuent. */
  listenMuted?: boolean;
}

export function useSlotAudioPreview(slotId: string, enabled: boolean, draft: SlotAudioPreviewDraft): void {
  const { audioSourceRegistry: registry } = useAudioMixer();
  const realId    = `audio-${slotId}`;
  const previewId = `audio-preview-${slotId}`;

  // Le registre n'est pas réactif : l'entrée réelle peut naître ou être recréée pendant
  // que la fenêtre est ouverte (changement de source audio) — on suit sa génération.
  const [generation, setGeneration] = useState(0);
  useEffect(() => {
    if (!enabled || !slotId) return;
    const timer = setInterval(() => {
      const g = registry.getGeneration(realId);
      setGeneration(prev => (prev === g ? prev : g));
    }, 400);
    return () => clearInterval(timer);
  }, [enabled, slotId, registry, realId]);

  const [ready, setReady] = useState(0);

  useEffect(() => {
    if (!enabled || !slotId || !registry.has(realId)) return;
    const stream = registry.getStream(realId);
    if (!stream) return;

    let handle: { release: () => void };
    try {
      handle = registry.borrowFromStream(previewId, stream, 'stream', false);
    } catch (err) {
      console.warn(`[useSlotAudioPreview] aperçu de ${slotId} impossible :`, err);
      return;
    }
    const savedMonitor = registry.getMonitorGain(realId);
    registry.setMonitorGain(realId, 0);
    registry.setMonitorGain(previewId, 1);
    setReady(n => n + 1);

    return () => {
      handle.release();
      // Rétablit l'écoute de l'entrée réelle si personne d'autre ne l'a changée entre-temps.
      if (savedMonitor !== null && registry.has(realId) && registry.getMonitorGain(realId) === 0) {
        registry.setMonitorGain(realId, savedMonitor);
      }
    };
  }, [enabled, slotId, registry, realId, previewId, generation]);

  // Lu à chaque changement du brouillon sans relancer l'emprunt ci-dessus.
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => {
    if (!enabled || !registry.has(previewId)) return;
    const d = draftRef.current;
    registry.setMuted(previewId, d.muted);
    registry.setMonitorGain(previewId, d.listenMuted ? 0 : 1);
    for (const effect of AUDIO_EFFECTS) {
      effect.apply(registry, previewId, !!d.effects[effect.id], d.effectConfigs[effect.id] ?? effect.defaultConfig);
    }
    if (!d.muted) {
      registry.setGain(previewId, d.volume);
      registry.setGainLeft(previewId, d.stereo.left);
      registry.setGainRight(previewId, d.stereo.right);
    }
    // Diagnostic (16/10/2026) : ce que les nœuds portent réellement, une fois les rampes
    // passées — l'aperçu est-il bien réglé, branché sur les enceintes, et l'entrée réelle coupée ?
    const timer = setTimeout(async () => {
      const actifs = AUDIO_EFFECTS.filter(e => d.effects[e.id]).map(e => e.id).join(',') || 'aucun';
      const post = await registry.measurePostMix(previewId);
      console.warn(
        `[PREVIEW] ${previewId} effets=[${actifs}] apresEffets=${JSON.stringify(post)}` +
        ` aperçu=${JSON.stringify(registry.describeEntry(previewId))}` +
        ` | réel=${JSON.stringify(registry.describeEntry(realId))}`
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [enabled, registry, previewId, realId, ready, draft.effects, draft.effectConfigs, draft.stereo.left, draft.stereo.right, draft.volume, draft.muted, draft.listenMuted]);
}
